import React, { useEffect, useState, useRef } from 'react';
import axiosInstance from '../../utils/axiosConfig';
import LoaderInside from '../LoaderInside';
import RatingHeader from './RatingHeader';
import { useSelector } from 'react-redux';

const RatingTable = () => {
    const [ratingData, setRatingData] = useState([]);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [stickyPosition, setStickyPosition] = useState(null); // 'top', 'bottom' или null
    const scrollContainerRef = useRef(null);
    const currentUserRowRef = useRef(null);
    const { telegramId: reduxTelegramId, userId: reduxUserId } = useSelector((state) => state.user);

    const fetchRatingData = () => {
        setIsLoading(true);
        axiosInstance.get('/rating/get')
            .then(response => {
                if (response.data.success) {
                    setRatingData(response.data.data);
                }
                setIsLoading(false);
            })
            .catch(err => {
                console.error('Ошибка при запросе рейтинга:', err);
                setIsLoading(false);
            });
    };

    useEffect(() => {
        // Получаем данные о рейтинге
        fetchRatingData();

        // Получаем ID текущего пользователя из Telegram initData
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const initDataStr = urlParams.get('tgWebAppData') || window.Telegram?.WebApp?.initData;

            if (initDataStr) {
                const initData = new URLSearchParams(initDataStr);
                const userStr = initData.get('user');

                if (userStr) {
                    const user = JSON.parse(userStr);
                    setCurrentUserId(user.id.toString());
                }
            } else if (window.Telegram?.WebApp?.initDataUnsafe?.user) {
                // Альтернативный способ получения данных пользователя
                setCurrentUserId(window.Telegram.WebApp.initDataUnsafe.user.id.toString());
            }
        } catch (error) {
            console.error('Ошибка при получении данных пользователя из Telegram:', error);
        }
    }, []);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        const currentUserRow = currentUserRowRef.current;

        if (!scrollContainer || !currentUserRow) return;

        const handleScroll = () => {
            const containerRect = scrollContainer.getBoundingClientRect();
            const rowRect = currentUserRow.getBoundingClientRect();

            // Проверяем, видна ли хотя бы часть строки пользователя
            const isPartiallyVisible =
                (rowRect.top < containerRect.bottom && rowRect.bottom > containerRect.top);

            if (isPartiallyVisible) {
                // Если хоть часть строки видна, то убираем sticky
                setStickyPosition(null);
            } else if (rowRect.bottom <= containerRect.top) {
                // Если строка выше видимой области
                setStickyPosition('top');
            } else if (rowRect.top >= containerRect.bottom) {
                // Если строка ниже видимой области
                setStickyPosition('bottom');
            }
        };

        scrollContainer.addEventListener('scroll', handleScroll);
        // Вызываем один раз после загрузки данных
        setTimeout(handleScroll, 1000);

        return () => {
            scrollContainer.removeEventListener('scroll', handleScroll);
        };
    }, [isLoading, ratingData, reduxUserId, reduxTelegramId, currentUserId]);

    const scrollToCurrentUser = () => {
        if (currentUserRowRef.current) {
            currentUserRowRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
            // Сбрасываем sticky позицию после прокрутки
            setTimeout(() => setStickyPosition(null), 500);
        }
    };

    const handleRefresh = () => {
        fetchRatingData();
    };

    // Функция для отображения имени игрока с приоритетом: Имя (если не "Аноним" и не пустое) -> username -> ID
    const getPlayerName = (item) => {
        const rawName = item?.name || item?.fullName || item?.full_name;
        const isBlank = (v) => !v || String(v).trim() === '';
        const isAnon = (v) => String(v).trim().toLowerCase() === 'аноним';

        if (!isBlank(rawName) && !isAnon(rawName)) {
            return String(rawName).trim();
        }

        if (!isBlank(item?.username)) {
            return String(item.username).trim();
        }

        return `ИГРОК ${item.telegramID}`;
    };

    if (isLoading) {
        return (
            <>
                <RatingHeader onRefresh={handleRefresh} />
                <div className="loader-container">
                    <LoaderInside />
                </div>
            </>
        );
    }

    // Найдем индекс текущего пользователя
    const resolvedTelegramId = reduxTelegramId || currentUserId;
    const matchesCurrentUser = (item) => {
        if (resolvedTelegramId && item.telegramID) {
            return item.telegramID === resolvedTelegramId;
        }

        if (reduxUserId && item.userId) {
            return item.userId === reduxUserId;
        }

        return false;
    };

    const currentUserIndex = ratingData.findIndex(matchesCurrentUser);

    return (
        <>
            <RatingHeader onRefresh={handleRefresh} />
            <div className="rating-page-container">
                <div className="corner top-left"></div>
                <div className="corner top-right"></div>
                <div className="corner bottom-left"></div>
                <div className="corner bottom-right"></div>

                <div className="rating-content-container">
                    <div className="rating-header">
                        <div className="header-cell">ПОЗИЦИЯ</div>
                        <div className="header-cell">ИГРОК</div>
                        <div className="header-cell">БАЛЛЫ</div>
                    </div>

                    {/* Отображение sticky-строки сверху при необходимости */}
                    {stickyPosition === 'top' && currentUserIndex !== -1 && (
                        <div className="sticky-user-row top" onClick={scrollToCurrentUser}>
                            <div className="rating-cell position">
                                {currentUserIndex + 1}
                            </div>
                            <div className="rating-cell player">
                                {getPlayerName(ratingData[currentUserIndex])}
                            </div>
                            <div className="rating-cell score">
                                {ratingData[currentUserIndex].totalScore}
                            </div>
                        </div>
                    )}

                    <div className="rating-scroll-container" ref={scrollContainerRef}>
                        <div className="rating-container">
                            <div className="rating-rows">
                                {ratingData.map((item, index) => (
                                    <div
                                        key={index}
                                        className={`rating-row ${matchesCurrentUser(item) ? 'current-user' : ''}`}
                                        ref={matchesCurrentUser(item) ? currentUserRowRef : null}
                                    >
                                        <div className="rating-cell position">
                                            {index + 1}
                                        </div>
                                        <div className="rating-cell player">
                                            {getPlayerName(item)}
                                        </div>
                                        <div className="rating-cell score">
                                            {item.totalScore}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Отображение sticky-строки внизу при необходимости */}
                    {stickyPosition === 'bottom' && currentUserIndex !== -1 && (
                        <div className="sticky-user-row bottom" onClick={scrollToCurrentUser}>
                            <div className="rating-cell position">
                                {currentUserIndex + 1}
                            </div>
                            <div className="rating-cell player">
                                {getPlayerName(ratingData[currentUserIndex])}
                            </div>
                            <div className="rating-cell score">
                                {ratingData[currentUserIndex].totalScore}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default RatingTable;
